import {
  ActivityIcon,
  BrainIcon,
  DatabaseIcon,
  FilesIcon,
  HouseIcon,
  type LucideIcon,
  PlusIcon,
  SquarePlusIcon,
} from "lucide-react";
import Link from "next/link";
import type { ComponentProps, MouseEventHandler, ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ChannelTab } from "./channel-view-options";

/*
 * The channel's section tabs as markup. ChannelOverviewActions owns the open
 * section, pane navigation and the channel's menus, and renders the tabs
 * through these, so a fixture channel composes the same rows.
 */

/** A section tab: its label, where it goes and the section it selects. */
export type ChannelSectionLink = {
  label: string;
  href: string;
  view: ChannelTab;
  icon: LucideIcon;
};

/** The channel's sections in tab order: Overview, Data, Feed, Memory and Files. */
export function channelSectionLinks(channelId: string): ChannelSectionLink[] {
  const channelHref = `/workspace/channels/${encodeURIComponent(channelId)}`;
  return [
    {
      label: "Overview",
      href: channelHref,
      view: "overview",
      icon: HouseIcon,
    },
    {
      label: "Data",
      href: `${channelHref}?view=data`,
      view: "data",
      icon: DatabaseIcon,
    },
    {
      label: "Feed",
      href: `${channelHref}?view=feed`,
      view: "feed",
      icon: ActivityIcon,
    },
    {
      label: "Memory",
      href: `${channelHref}?view=memory`,
      view: "memory",
      icon: BrainIcon,
    },
    {
      label: "Files",
      href: `${channelHref}?view=files`,
      view: "files",
      icon: FilesIcon,
    },
  ];
}

/** The section tabs twice, compact for a narrow channel and expanded for a wide one, each ending in the channel's actions. */
export function ChannelSectionTabsView({
  links,
  selectedView,
  compactActions,
  expandedActions,
  paneLink,
}: {
  links: ChannelSectionLink[];
  /** The open section; a data set's table, board or other view is part of Data. */
  selectedView: ChannelTab;
  compactActions?: ReactNode;
  expandedActions?: ReactNode;
  /** A link's click handler, where a session pane opens channel links in place. */
  paneLink?: (
    href: string,
  ) => MouseEventHandler<HTMLAnchorElement> | undefined;
}) {
  return (
    <>
      <nav
        aria-label="Channel sections"
        className="channel-sections-compact h-10 w-full shrink-0 items-center gap-1 border-b border-border/60 pl-1.5 pr-2 text-sm"
      >
        {/* Every tab stays one tap away: a narrow pane names only the open
            section (globals.css), and the row still scrolls if it must. A
            scrolling row clips at its padding edge, so its padding keeps the
            open tab's ring and shadow whole. */}
        <div className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto py-1 pl-0.5 pr-4 [mask-image:linear-gradient(to_right,black_calc(100%-1.5rem),transparent)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {links.map((link) => (
            <SectionLink
              key={link.label}
              link={link}
              selected={selectedView === link.view}
              onClick={paneLink?.(link.href)}
            />
          ))}
        </div>
        <div className="flex shrink-0 items-center gap-1">{compactActions}</div>
      </nav>

      <nav
        aria-label="Channel sections"
        className="channel-sections-expanded h-10 w-full shrink-0 items-center gap-1 border-b border-border/60 px-4 text-sm"
      >
        {links.map((link) => (
          <SectionLink
            key={link.label}
            link={link}
            selected={selectedView === link.view}
            onClick={paneLink?.(link.href)}
          />
        ))}
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {expandedActions}
        </div>
      </nav>
    </>
  );
}

/** Starts a session in the channel: an icon in the compact tabs, labelled in the expanded ones. */
export function ChannelNewSessionButton({
  href,
  compact = false,
}: {
  href: string;
  compact?: boolean;
}) {
  return compact ? (
    <Button variant="outline" size="icon-sm" nativeButton={false}
      aria-label="New session in this channel" title="New session in this channel"
      className="shrink-0"
      render={<Link href={href} />}>
      <SquarePlusIcon aria-hidden />
    </Button>
  ) : (
    <Button variant="outline" size="sm" nativeButton={false}
      className="shrink-0"
      render={<Link href={href} />}>
      <SquarePlusIcon data-icon="inline-start" aria-hidden />
      New session
    </Button>
  );
}

/** The channel's closed "Add" menu button; the menu's trigger props pass through. */
export function ChannelAddMenuTrigger(
  props: Omit<ComponentProps<typeof Button>, "children" | "size" | "variant">,
) {
  return (
    <Button size="sm" variant="ghost" aria-label="Add to channel" {...props}>
      <PlusIcon /> Add
    </Button>
  );
}

function SectionLink({
  link,
  selected,
  onClick,
}: {
  link: ChannelSectionLink;
  selected: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}) {
  const Icon = link.icon;
  return (
    <Link
      href={link.href}
      onClick={onClick}
      aria-current={selected ? "page" : undefined}
      title={link.label}
      className={cn(
        // The same look as the app's navigation tabs (TabsList variant="navigation").
        "channel-section-link inline-flex h-6.5 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2 text-xs font-medium transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        selected
          ? "bg-background text-foreground shadow-sm ring-1 ring-border/70"
          : "text-muted-foreground",
      )}
    >
      <Icon className="size-3.5" aria-hidden />
      <span className="channel-section-link-label">{link.label}</span>
    </Link>
  );
}
